import React from 'react';

import { UserAction, UserActionRecordDetailed } from '@go-shared/types/userActionRecord';
import { ActionChip } from '@go-client/components/UserRecords/UserRecords';
import { getLocaleUTCDateTimeString, timeToAgoString } from '@go-shared/lib/time';
import { PropertyLog } from '@go-client/components/UserRecords/Visualizations/PropertyLog';
import { EventTemplateLog } from '@go-client/components/UserRecords/Visualizations/EventTemplateLog';
import { RawLog } from '@go-client/components/UserRecords/Visualizations/RawLog';
import { VenueTemplateLog } from '@go-client/components/UserRecords/Visualizations/VenueTemplateLog';
import { syntaxHighlight } from '@go-client/components/UserRecords/Visualizations/utils';
import { ContentTemplateLog } from '@go-client/components/UserRecords/Visualizations/ContentTemplateLog';
import { StaffTemplateLog } from '@go-client/components/UserRecords/Visualizations/StaffTemplateLog';
import { ItineraryLog } from '@go-client/components/UserRecords/Visualizations/ItineraryLog';
import { BubbleBadge, BubbleButton } from '@go-client/components/BubbleUI';
import { LibraryTranslationsLog } from '@go-client/components/UserRecords/Visualizations/LibraryTranslationsLog';
import { UITranslationsLog } from '@go-client/components/UserRecords/Visualizations/UiTranslationsLog';
import { VenueLog } from '@go-client/components/UserRecords/Visualizations/VenueLog';
import { EventLog } from '@go-client/components/UserRecords/Visualizations/EventLog';
import { GroupBookingLog } from '@go-client/components/UserRecords/Visualizations/GroupBookingLog';
import { DayTemplateEventLog } from '@go-client/components/UserRecords/Visualizations/DayTemplateEventLog';
import { DayTemplateVenueDataLog } from '@go-client/components/UserRecords/Visualizations/DayTemplateVenueDataLog';
import { FeedLog } from '@go-client/components/UserRecords/Visualizations/FeedLog';
import { SailingTemplateLog } from '@go-client/components/UserRecords/Visualizations/SailingTemplateLog';
import { InlineIcon } from '@spark-labs/avocado-ui';
import { hashStringToColor } from '@go-shared/lib/utils';
import { ArchivedBadge } from '@go-client/components/Common/ArchivedBadge';
import { TemplateFragmentsLog } from '@go-client/components/UserRecords/Visualizations/TemplateFragmentsLog';

export type UserActionLog = UserActionRecordDetailed & {
  onClose?: () => void;
};

const ACTION_TO_COMPONENT = new Map<UserAction, React.FC<UserActionLog>>([
  ['PROPERTY CREATED', PropertyLog],
  ['PROPERTY UPDATED', PropertyLog],

  ['ITINERARY CREATED', ItineraryLog],
  ['ITINERARY UPDATED', ItineraryLog],

  ['EVENT TEMPLATE UPDATED', EventTemplateLog],
  ['EVENT TEMPLATE CREATED', EventTemplateLog],

  ['VENUE TEMPLATE CREATED', VenueTemplateLog],
  ['VENUE TEMPLATE UPDATED', VenueTemplateLog],

  ['CONTENT TEMPLATE CREATED', ContentTemplateLog],
  ['CONTENT TEMPLATE UPDATED', ContentTemplateLog],

  ['STAFF TEMPLATE CREATED', StaffTemplateLog],
  ['STAFF TEMPLATE UPDATED', StaffTemplateLog],

  ['LIBRARY TRANSLATIONS UPDATED', LibraryTranslationsLog],

  ['UI TRANSLATIONS UPDATED', UITranslationsLog],

  ['VENUE UPDATED', VenueLog],

  ['EVENT CREATED', EventLog],
  ['EVENT UPDATED', EventLog],

  ['FEED UPDATED', FeedLog],

  ['SAILING TEMPLATE UPDATED', SailingTemplateLog],

  ['TEMPLATE FRAGMENTS LINKED', TemplateFragmentsLog],
  ['TEMPLATE FRAGMENTS UNLINKED', TemplateFragmentsLog],

  // 'GROUP EVENT DELETED',  // Couldn't find the usages
  // 'GROUP EVENT CREATED',  // Couldn't find the usages
  // 'GROUP EVENT UPDATED',  // Couldn't find the usages

  ['GROUP BOOKING CREATED', GroupBookingLog],
  ['GROUP BOOKING UPDATED', GroupBookingLog],

  ['DAY TEMPLATE EVENT CREATED', DayTemplateEventLog],
  ['DAY TEMPLATE EVENT UPDATED', DayTemplateEventLog],

  ['DAY TEMPLATE VENUE DATA UPDATED', DayTemplateVenueDataLog],
]);

const getLogComponent = (action: UserAction) => {
  if (ACTION_TO_COMPONENT.has(action)) {
    return ACTION_TO_COMPONENT.get(action);
  }

  return RawLog;
};

export const UserActionLog: React.FC<UserActionLog> = (props: UserActionLog) => {
  const { body, action, onClose, method, url, user, createdAt, _id, query } = props;

  let bodySorted = Object.keys(body)
    .sort()
    .reduce((obj: { [key: string]: string }, key) => {
      obj[key] = body[key];
      return obj;
    }, {});

  const Component = getLogComponent(action);
  const componentProps = { ...props, body: bodySorted };

  let strategy = '';
  if (url.includes('template-fragment/unlink') && body?.linkFragments?.length) {
    strategy = body.linkFragments[0].unlinkStrategy === 'full' ? 'Unlink and Clear' : 'Unlink Only';
  }

  return (
    <div className="log">
      <div className="sticky top-0 bg-eggshell px-5 pt-5 pb-2">
        <div className="flex items-start ">
          <div className="mr-auto">
            <h2 className="text-2xl text-navy">
              <ActionChip action={action!} />

              {url.includes('archive') && (
                <>
                  {action === 'PROPERTY UPDATED' && (
                    <ArchivedBadge
                      variant="dirt"
                      text={query.newState === 'true' ? 'Archived' : 'Unarchived'}
                    />
                  )}
                  {action !== 'PROPERTY UPDATED' && (
                    <ArchivedBadge
                      variant="dirt"
                      text={body.archived ? 'Archived' : 'Unarchived'}
                    />
                  )}
                </>
              )}

              {url.includes('template-fragment/unlink') && (
                <>
                  <BubbleBadge color={hashStringToColor(strategy)} className="p-1-i">
                    {strategy}
                  </BubbleBadge>
                </>
              )}
            </h2>
          </div>
          <BubbleButton
            onClick={onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>

        <div className="block items-center mb-5">
          <InlineIcon name="user" className="text-grey w-5 h-5 mr-1" />
          <span>
            {user.firstName} {user.lastName}
          </span>

          <span className="time-span mr-1 mt-4 ml-5">
            {getLocaleUTCDateTimeString(new Date(createdAt))}
          </span>

          <span className="time-span mr-1 mt-4 ml-5">Log ID: {_id}</span>
        </div>

        <code className={`my-4 block url`}>
          <strong>{method}</strong> {url}
        </code>
      </div>

      <table className="p-5 m-5">
        <tbody>{Component && <Component {...componentProps} />}</tbody>
      </table>
    </div>
  );
};

const validDate = (date: string): boolean => {
  const d = new Date(date);
  return !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date.slice(0, 10);
};

export const LogRow: React.FC<{ field: string; value: any }> = ({ field, value }) => {
  let newValue: number | string | React.JSX.Element = value;

  // This function will 'resolve' and parse all the common or defaults fields
  // without having to do it manually on every log
  try {
    // @ts-ignore
    const parsed = JSON.parse(JSON.stringify(value)); // hacky way to check if it's a json
    const isArray = Array.isArray(parsed);
    const isObject = typeof parsed === 'object';
    const isDate = typeof value === 'string' && validDate(value);
    const isBoolean = typeof value === 'boolean';
    const isNumber = !isObject && !isArray && !isBoolean && !isNaN(value);

    if (isNumber) {
      newValue = Number(value);
    } else if (isArray || isObject) {
      newValue = (
        <>
          <span>{isArray && parsed.length > 1 && `(${parsed.length} elements)`}</span>
          <pre
            dangerouslySetInnerHTML={{
              __html: syntaxHighlight(JSON.stringify(value, undefined, 4)),
            }}
          />
        </>
      );
    } else if (isDate) {
      newValue = `${getLocaleUTCDateTimeString(new Date(value))} (${timeToAgoString(
        new Date(value),
        new Date()
      )})`;
    } else if (isBoolean) {
      newValue = String(value);
    }
  } catch (error) {
    newValue = value;
  }

  return (
    <>
      <tr className="mb-4 w-full">
        <td className="font-regular p-3 text-grey-darker font-bold w-1/4 align-text-top">
          {field}
        </td>
        <td className="font-regular ml-2 p-3 text-grey-dark w-3/4 text-left">{newValue}</td>
      </tr>
    </>
  );
};
